import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import GooglePlacesAddressInput from "../GooglePlacesAddressInput";
import { ConsoleCarouselOrderStrip } from "./ConsoleClubFormShared";
import PaceUnitToggle from "./PaceUnitToggle";
import ConsolePaceRangePills from "./ConsolePaceRangePills";
import {
  RunClubCarouselUrlSections,
  appendRunImageFileSlots,
  appendRunPastedUrlSlot,
} from "./ConsoleRunCarouselSections";
import {
  CONSOLE_TIMEZONE_OPTIONS,
  MAX_CAROUSEL_IMAGES,
  swapNeighbors,
} from "../../utils/consoleUtils";
import {
  EFFORT_OPTIONS,
  PACE_PILLS_MAX,
  REL_OPTIONS_CREATE,
  RUN_TYPES,
  SURFACE_OPTIONS,
} from "../../utils/consoleRunsConstants";

/**
 * Shared create/edit run dialog for console Runs (same fields as mobile run club create).
 * State and submit logic stay in RunsView; this component is presentational + local field handlers.
 */
export default function ConsoleRunFormModal({
  variant,
  open,
  onClose,
  title,
  description,
  form,
  onFieldChange,
  /** Called with GooglePlaces place object (formattedAddress, city, state). */
  onAddressPlaceSelected,
  whatToExpectRef,
  clubs,
  organizerLoading,
  organizerId,
  currentUserId,
  editSubtitle,
  clubGoodToKnow,
  onClubGoodToKnowChange,
  imageSlots,
  setImageSlots,
  imageBlobById,
  fileInputId,
  appendSlotKeyPrefix,
  clubForCarousel,
  onRemoveScrapedFromClub,
  scrapedRemoveBusyDisplayUrl,
  onSubmit,
  submitting,
  submitLabel,
  submitDisabled,
  recurringToggleLabel,
  /** Edit only: destructive delete (confirm + API handled in RunsView). */
  onDeleteRun,
  deleteRunBusy = false,
  /** Edit only: save pasted image URL immediately (import + PATCH images). */
  onSavePastedRunImageUrl,
  savePastedRunImageUrlBusy = false,
}) {
  const [runImagePasteDraft, setRunImagePasteDraft] = useState("");
  const showClubGtk = Boolean((form.club_id ?? "").trim());
  const showPasteUrlSave = variant === "edit" && typeof onSavePastedRunImageUrl === "function";
  const prefix = appendSlotKeyPrefix || "run";
  const addPastedRunImage = () => {
    const u = runImagePasteDraft.trim();
    if (!u.startsWith("http")) return;
    appendRunPastedUrlSlot(setImageSlots, u, prefix);
    setRunImagePasteDraft("");
  };

  useEffect(() => {
    if (open) setRunImagePasteDraft("");
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="console-page max-w-4xl max-h-[90vh] overflow-y-auto bg-[var(--console-surface)] border-[var(--console-border)] text-[var(--console-ink)]">
        <DialogHeader>
          <DialogTitle className="text-[var(--console-ink)]">{title}</DialogTitle>
          {description ? (
            <p className="text-sm text-[var(--console-muted)]">{description}</p>
          ) : null}
          {variant === "edit" && editSubtitle ? (
            <p className="text-sm text-[var(--console-muted)]">{editSubtitle}</p>
          ) : null}
        </DialogHeader>
        <div className="grid gap-4 py-4">
          {variant === "add" ? (
            <div className="grid gap-2">
              <label className="text-sm font-medium text-[var(--console-ink)]">
                Club <span className="text-[var(--console-red)]" aria-hidden="true">*</span>
              </label>
              <select
                value={form.club_id}
                onChange={(e) => onFieldChange("club_id", e.target.value)}
                className="w-full rounded border border-[var(--console-border)] bg-[var(--console-surface)] px-3 py-2 text-sm"
                aria-label="Club"
              >
                <option value="">Select club…</option>
                {(clubs || []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              {form.club_id && organizerLoading && (
                <p className="text-xs text-[var(--console-amber)] mt-1">Resolving organizer…</p>
              )}
              {form.club_id && !organizerLoading && organizerId === null && (
                <p className="text-xs text-[var(--console-red)] mt-1">
                  This club has no organizer; link an organizer before creating runs.
                </p>
              )}
            </div>
          ) : null}

          <div className="grid gap-2">
            <label className="text-sm font-medium text-[var(--console-ink)]">
              Run name <span className="text-[var(--console-red)]" aria-hidden="true">*</span> (max 30)
            </label>
            <Input
              value={form.name}
              onChange={(e) => onFieldChange("name", e.target.value)}
              placeholder="e.g. Tuesday Tempo"
              maxLength={31}
              className="border-[var(--console-border)] bg-[var(--console-surface)]"
            />
          </div>
          <div className="grid gap-2">
            <label className="text-sm font-medium text-[var(--console-ink)]">
              Date & time <span className="text-[var(--console-red)]" aria-hidden="true">*</span>
            </label>
            <input
              type="datetime-local"
              value={form.scheduledDateTimeLocal}
              onChange={(e) => onFieldChange("scheduledDateTimeLocal", e.target.value)}
              className="w-full rounded border border-[var(--console-border)] bg-[var(--console-surface)] px-3 py-2 text-sm"
              aria-label="Date and time"
            />
            <p className="text-xs text-[var(--console-muted)]">
              {form.timezone
                ? "Date & time are in the selected run timezone."
                : "Uses your browser's local timezone. For runs abroad, select a timezone below."}
            </p>
          </div>
          <div className="grid gap-2">
            <label className="text-sm font-medium text-[var(--console-ink)]">
              Timezone <span className="text-[var(--console-muted)] font-normal">(optional)</span>
            </label>
            <select
              value={form.timezone}
              onChange={(e) => onFieldChange("timezone", e.target.value)}
              className="w-full rounded border border-[var(--console-border)] bg-[var(--console-surface)] px-3 py-2 text-sm text-[var(--console-ink)]"
              aria-label="Run timezone"
            >
              {CONSOLE_TIMEZONE_OPTIONS.map((opt) => (
                <option key={opt.value || "__empty__"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="grid gap-2">
            <label className="text-sm font-medium text-[var(--console-ink)]">
              Address <span className="text-[var(--console-red)]" aria-hidden="true">*</span>
            </label>
            <GooglePlacesAddressInput
              value={form.address}
              onChange={(val) => onFieldChange("address", val)}
              onPlaceSelected={(place) => onAddressPlaceSelected(place)}
              placeholder="Full meet-up address"
              maxLength={200}
              className="border-[var(--console-border)] bg-[var(--console-surface)] rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div className="grid gap-2">
            <label className="text-sm font-medium text-[var(--console-ink)]">What to expect</label>
            <Textarea
              ref={whatToExpectRef}
              value={form.what_to_expect}
              onChange={(e) => onFieldChange("what_to_expect", e.target.value)}
              placeholder="Pace, distance, workout details…"
              rows={1}
              className="border-[var(--console-border)] bg-[var(--console-surface)] resize-none min-h-[60px] overflow-hidden"
            />
          </div>
          {showClubGtk ? (
            <div className="grid gap-2">
              <label className="text-sm font-medium text-[var(--console-ink)]">
                Good to know <span className="text-[var(--console-muted)] font-normal">(club, max 3)</span>
              </label>
              <p className="text-xs text-[var(--console-muted)]">
                Bullet notes for this club; they appear on run cards in the app (same as Clubs → Club notes).
              </p>
              {[0, 1, 2].map((i) => (
                <Input
                  key={`run-gtk-${variant}-${i}`}
                  value={clubGoodToKnow[i] ?? ""}
                  onChange={(e) => onClubGoodToKnowChange(i, e.target.value)}
                  placeholder={`Good to know ${i + 1}`}
                  className="border-[var(--console-border)] bg-[var(--console-surface)]"
                  autoComplete="off"
                />
              ))}
            </div>
          ) : null}
          <div className="grid gap-2">
            <label className="text-sm font-medium text-[var(--console-ink)]">
              Run image(s){" "}
              <span className="text-[var(--console-muted)] font-normal">
                (optional, max {MAX_CAROUSEL_IMAGES})
              </span>
            </label>
            <p className="text-xs text-[var(--console-muted)]">
              {variant === "add"
                ? "First slide is the primary run image. Add files, paste HTTPS URLs (stored on our bucket on save), or pick from the club sections below."
                : "Reorder with arrows; first slide is primary. Pasted URLs are re-fetched to our bucket when you save, or use Save next to the URL field to import without closing."}
            </p>
            <ConsoleCarouselOrderStrip
              entries={imageSlots.map((s, i) => ({
                id: s.id,
                previewSrc:
                  s.kind === "url" || s.kind === "pasted_url" ? s.url : imageBlobById[s.id] || "",
                subtitle:
                  s.kind === "pasted_url"
                    ? "Pasted URL"
                    : s.kind === "url"
                      ? `Slide ${i + 1}`
                      : (s.file?.name || "Upload").slice(0, 24),
              }))}
              onMoveUp={(i) => setImageSlots((prev) => swapNeighbors(prev, i, -1))}
              onMoveDown={(i) => setImageSlots((prev) => swapNeighbors(prev, i, 1))}
              onRemove={(i) => setImageSlots((prev) => prev.filter((_, j) => j !== i))}
              emptyHint={
                variant === "add"
                  ? "No images yet — upload files, paste a URL below, or add from the club sections below."
                  : "No images — uploads, paste a URL, or club picks below."
              }
            />
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-end">
              <div className="flex flex-1 min-w-0 flex-col gap-1">
                <label htmlFor={`${fileInputId}-paste-url`} className="text-xs font-medium text-[var(--console-muted)]">
                  Paste image URL (https)
                </label>
                <div className="flex flex-wrap gap-2">
                  <Input
                    id={`${fileInputId}-paste-url`}
                    type="url"
                    inputMode="url"
                    placeholder="https://…"
                    value={runImagePasteDraft}
                    onChange={(e) => setRunImagePasteDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addPastedRunImage();
                      }
                    }}
                    disabled={imageSlots.length >= MAX_CAROUSEL_IMAGES}
                    className="flex-1 min-w-[12rem] border-[var(--console-border)] bg-[var(--console-surface)]"
                    autoComplete="off"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="tb-btn shrink-0"
                    disabled={imageSlots.length >= MAX_CAROUSEL_IMAGES}
                    onClick={addPastedRunImage}
                  >
                    Add URL
                  </Button>
                  {showPasteUrlSave ? (
                    <Button
                      type="button"
                      variant="outline"
                      className="tb-btn shrink-0"
                      disabled={
                        imageSlots.length >= MAX_CAROUSEL_IMAGES ||
                        submitting ||
                        savePastedRunImageUrlBusy
                      }
                      onClick={async () => {
                        const u = runImagePasteDraft.trim();
                        if (!u.startsWith("http")) return;
                        const ok = await onSavePastedRunImageUrl(u);
                        if (ok) setRunImagePasteDraft("");
                      }}
                    >
                      {savePastedRunImageUrlBusy ? "Saving…" : "Save"}
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label
                htmlFor={fileInputId}
                className="cursor-pointer rounded border-0 bg-[#1A1916] px-3 py-1.5 text-white text-sm inline-block"
              >
                Choose Files
              </label>
              <input
                id={fileInputId}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  appendRunImageFileSlots(setImageSlots, files, appendSlotKeyPrefix);
                  e.target.value = "";
                }}
                className="sr-only"
                aria-label="Run images"
              />
            </div>
            {imageSlots.length > 0 && (
              <span className="text-xs text-[var(--console-muted)]">
                {imageSlots.length}/{MAX_CAROUSEL_IMAGES} in run carousel order.
              </span>
            )}
          </div>
          {showClubGtk && clubForCarousel ? (
            <RunClubCarouselUrlSections
              club={clubForCarousel}
              slots={imageSlots}
              setSlots={setImageSlots}
              mode={variant === "add" ? "add" : "edit"}
              heroReorderHint={
                variant === "add"
                  ? "Append club images to the ordered list above (reorder there)."
                  : "Append to the ordered list above (reorder there)."
              }
              scrapedHint="Use any of these as run images (from club scraped_images when present)."
              onRemoveScrapedFromClub={onRemoveScrapedFromClub}
              scrapedRemoveBusyDisplayUrl={scrapedRemoveBusyDisplayUrl}
            />
          ) : null}
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-2">
              <label className="text-sm font-medium text-[var(--console-ink)]">Run type</label>
              <select
                value={form.event_type}
                onChange={(e) => onFieldChange("event_type", e.target.value)}
                className="w-full rounded border border-[var(--console-border)] bg-[var(--console-surface)] px-3 py-2 text-sm"
              >
                {RUN_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid gap-2">
              <label className="text-sm font-medium text-[var(--console-ink)]">Effort</label>
              <select
                value={form.effort}
                onChange={(e) => onFieldChange("effort", e.target.value)}
                className="w-full rounded border border-[var(--console-border)] bg-[var(--console-surface)] px-3 py-2 text-sm"
              >
                {EFFORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.value}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid gap-2">
            <label className="text-sm font-medium text-[var(--console-ink)]">Surface</label>
            <select
              value={form.surface}
              onChange={(e) => onFieldChange("surface", e.target.value)}
              className="w-full rounded border border-[var(--console-border)] bg-[var(--console-surface)] px-3 py-2 text-sm"
            >
              {SURFACE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="grid gap-2 text-left">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <label className="text-sm font-medium text-[var(--console-ink)]">
                Pace ranges (optional, max {PACE_PILLS_MAX})
              </label>
              <PaceUnitToggle />
            </div>
            <ConsolePaceRangePills
              selectedValues={form.paces}
              onPacesChange={(newPaces) => onFieldChange("paces", newPaces)}
            />
            <span className="text-xs text-[var(--console-muted)]">Values are stored as min/mi labels</span>
          </div>
          <div className="flex flex-col gap-3 items-start text-left">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_public !== false}
                onChange={(e) => onFieldChange("is_public", e.target.checked)}
                className="rounded border-[var(--console-border)]"
              />
              <span className="text-sm text-[var(--console-ink)]">Public run</span>
            </label>
            <div className="grid gap-2">
              <label className="text-sm font-medium text-[var(--console-ink)]">Reliability</label>
              <select
                value={form.reliability}
                onChange={(e) => onFieldChange("reliability", e.target.value)}
                className="w-full max-w-xs rounded border border-[var(--console-border)] bg-[var(--console-surface)] px-2 py-1 text-sm text-left"
              >
                {REL_OPTIONS_CREATE.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex flex-col gap-2 items-start text-left">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={(form.recurrence ?? "one-time") !== "one-time"}
                onChange={(e) => {
                  const makeRecurring = e.target.checked;
                  onFieldChange("recurrence", makeRecurring ? "weekly" : "one-time");
                }}
                className="rounded border-[var(--console-border)]"
              />
              <span className="text-sm text-[var(--console-ink)]">{recurringToggleLabel}</span>
            </label>
            {(form.recurrence ?? "one-time") !== "one-time" && (
              <div className="flex flex-wrap gap-2 items-center">
                {variant === "add" ? (
                  <span className="text-sm font-medium text-[var(--console-ink)]">Recurrence</span>
                ) : null}
                {[
                  { value: "daily", label: "Daily" },
                  { value: "weekly", label: "Weekly" },
                  { value: "monthly", label: "Monthly" },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => onFieldChange("recurrence", opt.value)}
                    className={`px-3 py-1.5 rounded text-sm border ${
                      form.recurrence === opt.value
                        ? "bg-[var(--console-accent)] text-white border-[var(--console-accent)]"
                        : "border-[var(--console-border)] bg-[var(--console-surface)] text-[var(--console-ink)]"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="border-t border-[var(--console-border)] pt-4 mt-4 flex flex-row flex-wrap items-center justify-between gap-2">
          {variant === "edit" && typeof onDeleteRun === "function" ? (
            <Button
              type="button"
              variant="destructive"
              className="tb-btn"
              onClick={onDeleteRun}
              disabled={submitting || deleteRunBusy}
            >
              {deleteRunBusy ? "Deleting…" : "Delete run"}
            </Button>
          ) : (
            <span className="min-w-0" aria-hidden="true" />
          )}
          <div className="flex flex-wrap gap-2 justify-end ml-auto">
            <Button type="button" variant="outline" className="tb-btn" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="button" className="tb-btn primary" onClick={onSubmit} disabled={submitting || submitDisabled}>
              {submitLabel}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
